import { Injectable, inject, signal, OnDestroy } from '@angular/core';
import { Subject, Observable, interval, of } from 'rxjs';
import { takeUntil, map, switchMap, startWith } from 'rxjs/operators';
import { WebSocketService } from './websocket.service';
import { environment } from '../../environments/environment';

export interface DriverLocation {
  driverId: number;
  name: string;
  vehicle: string;
  plate: string;
  lat: number;
  lng: number;
  heading: number;
  speed: number;
  status: 'AVAILABLE' | 'ON_TRIP' | 'OFFLINE';
  tripId?: number;
  timestamp: string;
  country?: string;
  state?: string;
  city?: string;
}

export interface RealtimeNotification {
  id: string;
  type: 'GEOFENCE_ALERT' | 'TRIP_UPDATE' | 'PROMO_REDEEM' | 'COMMISSION' | 'SYSTEM';
  title: string;
  message: string;
  severity: 'info' | 'warn' | 'error' | 'success';
  timestamp: Date;
  read: boolean;
  data?: any;
}

export interface EtaInfo {
  driverId: number;
  tripId: number;
  etaMinutes: number;
  distanceKm: number;
  updatedAt: Date;
}

export interface MockDriver {
  driverId: number;
  name: string;
  vehicle: string;
  plate: string;
  status: 'AVAILABLE' | 'ON_TRIP' | 'OFFLINE';
  route: { lat: number; lng: number }[];
  routeIndex: number;
  tripId?: number;
  tripPickup?: string;
  tripDropoff?: string;
  country: string;
  state: string;
  city: string;
}

const NYC_ROUTES: { lat: number; lng: number }[][] = [
  // Route 1: Midtown Manhattan loop (Times Square → Bryant Park → Grand Central → back)
  [
    { lat: 40.7580, lng: -73.9855 }, { lat: 40.7565, lng: -73.9843 }, { lat: 40.7549, lng: -73.9832 },
    { lat: 40.7536, lng: -73.9820 }, { lat: 40.7527, lng: -73.9782 }, { lat: 40.7540, lng: -73.9770 },
    { lat: 40.7553, lng: -73.9758 }, { lat: 40.7566, lng: -73.9746 }, { lat: 40.7579, lng: -73.9760 },
    { lat: 40.7590, lng: -73.9780 }, { lat: 40.7595, lng: -73.9810 }, { lat: 40.7590, lng: -73.9840 },
  ],
  // Route 2: Lower Manhattan (Wall St → Battery Park → WTC → back)
  [
    { lat: 40.7074, lng: -74.0113 }, { lat: 40.7060, lng: -74.0130 }, { lat: 40.7040, lng: -74.0145 },
    { lat: 40.7025, lng: -74.0160 }, { lat: 40.7010, lng: -74.0170 }, { lat: 40.7015, lng: -74.0135 },
    { lat: 40.7030, lng: -74.0120 }, { lat: 40.7045, lng: -74.0105 }, { lat: 40.7060, lng: -74.0095 },
    { lat: 40.7075, lng: -74.0100 },
  ],
  // Route 3: Upper East Side (Museum Mile → Central Park → Madison Ave)
  [
    { lat: 40.7794, lng: -73.9632 }, { lat: 40.7810, lng: -73.9620 }, { lat: 40.7825, lng: -73.9610 },
    { lat: 40.7840, lng: -73.9605 }, { lat: 40.7835, lng: -73.9640 }, { lat: 40.7820, lng: -73.9655 },
    { lat: 40.7805, lng: -73.9670 }, { lat: 40.7790, lng: -73.9660 }, { lat: 40.7785, lng: -73.9645 },
  ],
  // Route 4: SoHo / Greenwich Village
  [
    { lat: 40.7234, lng: -74.0020 }, { lat: 40.7245, lng: -74.0000 }, { lat: 40.7260, lng: -73.9980 },
    { lat: 40.7275, lng: -73.9965 }, { lat: 40.7290, lng: -73.9950 }, { lat: 40.7280, lng: -73.9975 },
    { lat: 40.7265, lng: -73.9990 }, { lat: 40.7250, lng: -74.0005 }, { lat: 40.7240, lng: -74.0015 },
  ],
  // Route 5: Brooklyn Bridge → DUMBO → Brooklyn Heights
  [
    { lat: 40.7061, lng: -73.9969 }, { lat: 40.7040, lng: -73.9935 }, { lat: 40.7025, lng: -73.9905 },
    { lat: 40.7030, lng: -73.9870 }, { lat: 40.7018, lng: -73.9860 }, { lat: 40.6990, lng: -73.9880 },
    { lat: 40.6975, lng: -73.9910 }, { lat: 40.6960, lng: -73.9940 }, { lat: 40.6980, lng: -73.9955 },
    { lat: 40.7010, lng: -73.9960 }, { lat: 40.7040, lng: -73.9965 },
  ],
  // Route 6: Chelsea → Meatpacking → Hudson Yards
  [
    { lat: 40.7465, lng: -74.0014 }, { lat: 40.7450, lng: -74.0030 }, { lat: 40.7435, lng: -74.0050 },
    { lat: 40.7420, lng: -74.0070 }, { lat: 40.7410, lng: -74.0055 }, { lat: 40.7425, lng: -74.0035 },
    { lat: 40.7440, lng: -74.0020 }, { lat: 40.7455, lng: -74.0005 }, { lat: 40.7470, lng: -73.9998 },
  ],
  // Route 7: Harlem → Morningside Heights
  [
    { lat: 40.8116, lng: -73.9465 }, { lat: 40.8100, lng: -73.9480 }, { lat: 40.8085, lng: -73.9500 },
    { lat: 40.8070, lng: -73.9520 }, { lat: 40.8055, lng: -73.9540 }, { lat: 40.8065, lng: -73.9555 },
    { lat: 40.8080, lng: -73.9535 }, { lat: 40.8095, lng: -73.9510 }, { lat: 40.8110, lng: -73.9485 },
  ],
  // Route 8: JFK Airport area loop
  [
    { lat: 40.6413, lng: -73.7781 }, { lat: 40.6430, lng: -73.7760 }, { lat: 40.6450, lng: -73.7740 },
    { lat: 40.6470, lng: -73.7720 }, { lat: 40.6460, lng: -73.7750 }, { lat: 40.6440, lng: -73.7770 },
    { lat: 40.6425, lng: -73.7790 }, { lat: 40.6415, lng: -73.7785 },
  ],
  // Route 9: LaGuardia Airport area
  [
    { lat: 40.7769, lng: -73.8740 }, { lat: 40.7780, lng: -73.8720 }, { lat: 40.7790, lng: -73.8700 },
    { lat: 40.7785, lng: -73.8680 }, { lat: 40.7770, lng: -73.8695 }, { lat: 40.7760, lng: -73.8715 },
    { lat: 40.7755, lng: -73.8735 },
  ],
  // Route 10: East Village → Lower East Side
  [
    { lat: 40.7264, lng: -73.9818 }, { lat: 40.7250, lng: -73.9830 }, { lat: 40.7235, lng: -73.9845 },
    { lat: 40.7220, lng: -73.9855 }, { lat: 40.7210, lng: -73.9870 }, { lat: 40.7200, lng: -73.9860 },
    { lat: 40.7215, lng: -73.9840 }, { lat: 40.7230, lng: -73.9825 }, { lat: 40.7248, lng: -73.9815 },
  ],
  // Route 11: Penn Station → Herald Square → Empire State
  [
    { lat: 40.7505, lng: -73.9934 }, { lat: 40.7500, lng: -73.9910 }, { lat: 40.7495, lng: -73.9885 },
    { lat: 40.7484, lng: -73.9857 }, { lat: 40.7490, lng: -73.9870 }, { lat: 40.7498, lng: -73.9895 },
    { lat: 40.7502, lng: -73.9915 }, { lat: 40.7508, lng: -73.9930 },
  ],
  // Route 12: Williamsburg → Greenpoint (Brooklyn)
  [
    { lat: 40.7081, lng: -73.9571 }, { lat: 40.7100, lng: -73.9555 }, { lat: 40.7120, lng: -73.9540 },
    { lat: 40.7140, lng: -73.9525 }, { lat: 40.7160, lng: -73.9510 }, { lat: 40.7150, lng: -73.9530 },
    { lat: 40.7130, lng: -73.9545 }, { lat: 40.7110, lng: -73.9560 }, { lat: 40.7090, lng: -73.9568 },
  ],
  // Route 13: Astoria Queens
  [
    { lat: 40.7720, lng: -73.9300 }, { lat: 40.7735, lng: -73.9280 }, { lat: 40.7750, lng: -73.9260 },
    { lat: 40.7765, lng: -73.9245 }, { lat: 40.7755, lng: -73.9265 }, { lat: 40.7740, lng: -73.9285 },
    { lat: 40.7725, lng: -73.9295 },
  ],
  // Route 14: Columbus Circle → Lincoln Center → UWS
  [
    { lat: 40.7681, lng: -73.9819 }, { lat: 40.7695, lng: -73.9830 }, { lat: 40.7710, lng: -73.9845 },
    { lat: 40.7725, lng: -73.9855 }, { lat: 40.7740, lng: -73.9860 }, { lat: 40.7730, lng: -73.9850 },
    { lat: 40.7715, lng: -73.9838 }, { lat: 40.7700, lng: -73.9825 }, { lat: 40.7688, lng: -73.9815 },
  ],
];

const MOCK_DRIVERS: Omit<MockDriver, 'route' | 'routeIndex'>[] = [
  { driverId: 1, name: 'Carlos Rivera', vehicle: 'Toyota Camry 2024', plate: 'NYC-4821', status: 'AVAILABLE', country: 'US', state: 'NY', city: 'New York City' },
  { driverId: 2, name: 'Aisha Johnson', vehicle: 'Honda Accord 2023', plate: 'NYC-7335', status: 'ON_TRIP', tripId: 10051, tripPickup: '350 5th Ave', tripDropoff: 'JFK Terminal 4', country: 'US', state: 'NY', city: 'New York City' },
  { driverId: 3, name: 'Wei Zhang', vehicle: 'Tesla Model 3', plate: 'NYC-1098', status: 'AVAILABLE', country: 'US', state: 'NY', city: 'New York City' },
  { driverId: 4, name: 'Fatima Hassan', vehicle: 'Hyundai Sonata 2024', plate: 'NYC-5502', status: 'ON_TRIP', tripId: 10053, tripPickup: 'Grand Central', tripDropoff: 'Wall St', country: 'US', state: 'NY', city: 'New York City' },
  { driverId: 5, name: 'James Wilson', vehicle: 'Chevrolet Malibu 2023', plate: 'NYC-8814', status: 'AVAILABLE', country: 'US', state: 'NY', city: 'New York City' },
  { driverId: 6, name: 'Sara Lopez', vehicle: 'Nissan Altima 2024', plate: 'NYC-3267', status: 'AVAILABLE', country: 'US', state: 'NY', city: 'New York City' },
  { driverId: 7, name: 'Ahmed Khan', vehicle: 'Ford Fusion 2023', plate: 'NYC-9041', status: 'ON_TRIP', tripId: 10055, tripPickup: 'Penn Station', tripDropoff: 'Brooklyn Heights', country: 'US', state: 'NY', city: 'New York City' },
  { driverId: 8, name: 'Yuki Tanaka', vehicle: 'BMW 330i 2024', plate: 'NYC-6173', status: 'AVAILABLE', country: 'US', state: 'NY', city: 'New York City' },
  { driverId: 9, name: 'Priya Sharma', vehicle: 'Kia K5 2024', plate: 'NYC-2456', status: 'OFFLINE', country: 'US', state: 'NY', city: 'New York City' },
  { driverId: 10, name: 'Marcus Brown', vehicle: 'Volkswagen Jetta 2023', plate: 'NYC-7789', status: 'AVAILABLE', country: 'US', state: 'NY', city: 'New York City' },
  { driverId: 11, name: 'Elena Petrov', vehicle: 'Mercedes C300 2024', plate: 'NYC-0315', status: 'ON_TRIP', tripId: 10058, tripPickup: 'Times Square', tripDropoff: 'LaGuardia', country: 'US', state: 'NY', city: 'New York City' },
  { driverId: 12, name: 'David Chen', vehicle: 'Subaru Legacy 2023', plate: 'NYC-4590', status: 'AVAILABLE', country: 'US', state: 'NY', city: 'New York City' },
  { driverId: 13, name: 'Isabella Garcia', vehicle: 'Audi A4 2024', plate: 'NYC-8126', status: 'OFFLINE', country: 'US', state: 'NY', city: 'New York City' },
  { driverId: 14, name: 'Omar Ali', vehicle: 'Lexus ES 2024', plate: 'NYC-5637', status: 'AVAILABLE', country: 'US', state: 'NY', city: 'New York City' },
];

@Injectable({ providedIn: 'root' })
export class RealtimeService implements OnDestroy {
  private ws = inject(WebSocketService);
  private destroy$ = new Subject<void>();

  notifications = signal<RealtimeNotification[]>([]);
  unreadCount = signal(0);
  driverLocations = signal<Map<number, DriverLocation>>(new Map());
  connected = signal(false);
  mockDriverPool = signal<MockDriver[]>([]);

  connect() {
    if (this.connected()) return;
    if (environment.bypassAuth || environment.useMockData) {
      this.startMockStreams();
      return;
    }
    this.ws.connect();
    this.connected.set(true);
    this.subscribeTopics();
  }

  getAvailableDrivers(): MockDriver[] {
    return this.mockDriverPool().filter(d => d.status === 'AVAILABLE');
  }

  assignDriverToTrip(driverId: number, tripId: number, pickup: string, dropoff: string) {
    this.mockDriverPool.update(pool => pool.map(d => {
      if (d.driverId === driverId) {
        return { ...d, status: 'ON_TRIP' as const, tripId, tripPickup: pickup, tripDropoff: dropoff };
      }
      return d;
    }));
    this.addNotification({
      id: crypto.randomUUID(),
      type: 'TRIP_UPDATE',
      title: 'Driver Assigned',
      message: `Driver ${this.mockDriverPool().find(d => d.driverId === driverId)?.name} assigned to Trip #${tripId}`,
      severity: 'success',
      timestamp: new Date(),
      read: false,
    });
  }

  completeTrip(driverId: number) {
    this.mockDriverPool.update(pool => pool.map(d => {
      if (d.driverId === driverId) {
        return { ...d, status: 'AVAILABLE' as const, tripId: undefined, tripPickup: undefined, tripDropoff: undefined };
      }
      return d;
    }));
  }

  private subscribeTopics() {
    this.ws.watch<DriverLocation>('/topic/drivers/location')
      .pipe(takeUntil(this.destroy$))
      .subscribe(loc => this.updateDriverLocation(loc));

    this.ws.watch<RealtimeNotification>('/topic/notifications')
      .pipe(takeUntil(this.destroy$))
      .subscribe(n => this.addNotification(n));
  }

  watchDriverLocation(driverId: number): Observable<DriverLocation> {
    if (environment.bypassAuth || environment.useMockData) {
      return this.mockDriverStream(driverId);
    }
    return this.ws.watch<DriverLocation>(`/topic/driver/${driverId}/location`)
      .pipe(takeUntil(this.destroy$));
  }

  getEta(driverId: number): Observable<EtaInfo> {
    if (environment.bypassAuth || environment.useMockData) {
      const driver = this.mockDriverPool().find(d => d.driverId === driverId);
      const remainSteps = driver ? Math.max(1, driver.route.length - driver.routeIndex) : 5;
      return interval(3000).pipe(
        startWith(0),
        map(i => ({
          driverId, tripId: driver?.tripId ?? 0,
          etaMinutes: Math.max(1, Math.round(remainSteps * 0.4 - i * 0.3)),
          distanceKm: Math.max(0.1, remainSteps * 0.15 - i * 0.1),
          updatedAt: new Date(),
        })),
        takeUntil(this.destroy$),
      );
    }
    return this.ws.watch<EtaInfo>(`/topic/trip/${driverId}/eta`)
      .pipe(takeUntil(this.destroy$));
  }

  markAsRead(id: string) {
    this.notifications.update(list =>
      list.map(n => n.id === id ? { ...n, read: true } : n)
    );
    this.unreadCount.set(this.notifications().filter(n => !n.read).length);
  }

  markAllRead() {
    this.notifications.update(list => list.map(n => ({ ...n, read: true })));
    this.unreadCount.set(0);
  }

  clearAll() {
    this.notifications.set([]);
    this.unreadCount.set(0);
  }

  private addNotification(n: RealtimeNotification) {
    this.notifications.update(list => [n, ...list].slice(0, 100));
    this.unreadCount.update(c => c + 1);
  }

  private updateDriverLocation(loc: DriverLocation) {
    this.driverLocations.update(m => {
      const copy = new Map(m);
      copy.set(loc.driverId, loc);
      return copy;
    });
  }

  private startMockStreams() {
    this.connected.set(true);
    this.seedMockNotifications();
    this.initMockDriverPool();

    interval(2000).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.tickDriverPositions();
    });

    interval(15000).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.simulateRandomEvents();
    });

    interval(8000).pipe(takeUntil(this.destroy$)).subscribe(() => {
      const msgs = [
        { type: 'GEOFENCE_ALERT' as const, title: 'Geofence Alert', message: `Driver entered Manhattan Core zone`, severity: 'warn' as const },
        { type: 'TRIP_UPDATE' as const, title: 'Trip Completed', message: `Trip #${10040 + Math.floor(Math.random() * 20)} completed — $${(15 + Math.random() * 50).toFixed(2)}`, severity: 'success' as const },
        { type: 'PROMO_REDEEM' as const, title: 'Promo Redeemed', message: `Code SUMMER2026 used by Passenger #${100 + Math.floor(Math.random() * 50)}`, severity: 'info' as const },
        { type: 'COMMISSION' as const, title: 'Commission Processed', message: `Driver earned $${(10 + Math.random() * 40).toFixed(2)} commission`, severity: 'success' as const },
        { type: 'SYSTEM' as const, title: 'System Notice', message: 'Peak hours detected — surge pricing active', severity: 'info' as const },
      ];
      const m = msgs[Math.floor(Math.random() * msgs.length)];
      this.addNotification({ id: crypto.randomUUID(), ...m, timestamp: new Date(), read: false });
    });
  }

  private initMockDriverPool() {
    const pool: MockDriver[] = MOCK_DRIVERS.map((d, i) => ({
      ...d,
      route: NYC_ROUTES[i % NYC_ROUTES.length],
      routeIndex: Math.floor(Math.random() * NYC_ROUTES[i % NYC_ROUTES.length].length),
    }));
    this.mockDriverPool.set(pool);

    for (const d of pool) {
      if (d.status !== 'OFFLINE') {
        const pt = d.route[d.routeIndex];
        this.updateDriverLocation({
          driverId: d.driverId, name: d.name, vehicle: d.vehicle, plate: d.plate,
          lat: pt.lat, lng: pt.lng, heading: 0, speed: 0,
          status: d.status, tripId: d.tripId, timestamp: new Date().toISOString(),
          country: d.country, state: d.state, city: d.city,
        });
      }
    }
  }

  private tickDriverPositions() {
    const pool = this.mockDriverPool();
    for (const d of pool) {
      if (d.status === 'OFFLINE') continue;

      d.routeIndex = (d.routeIndex + 1) % d.route.length;
      const curr = d.route[d.routeIndex];
      const prev = d.route[(d.routeIndex - 1 + d.route.length) % d.route.length];

      const heading = Math.atan2(curr.lng - prev.lng, curr.lat - prev.lat) * (180 / Math.PI);
      const jitter = (Math.random() - 0.5) * 0.0003;
      const speed = d.status === 'ON_TRIP' ? 30 + Math.random() * 30 : 10 + Math.random() * 25;

      this.updateDriverLocation({
        driverId: d.driverId, name: d.name, vehicle: d.vehicle, plate: d.plate,
        lat: curr.lat + jitter, lng: curr.lng + jitter,
        heading: (heading + 360) % 360, speed,
        status: d.status, tripId: d.tripId, timestamp: new Date().toISOString(),
        country: d.country, state: d.state, city: d.city,
      });
    }
  }

  private simulateRandomEvents() {
    const pool = this.mockDriverPool();
    const onTrip = pool.filter(d => d.status === 'ON_TRIP');
    if (onTrip.length > 0 && Math.random() > 0.5) {
      const d = onTrip[Math.floor(Math.random() * onTrip.length)];
      if (d.routeIndex > d.route.length * 0.7) {
        this.completeTrip(d.driverId);
        this.addNotification({
          id: crypto.randomUUID(), type: 'TRIP_UPDATE', title: 'Trip Completed',
          message: `${d.name} completed Trip #${d.tripId} — $${(18 + Math.random() * 45).toFixed(2)}`,
          severity: 'success', timestamp: new Date(), read: false,
        });
      }
    }

    const offline = pool.filter(d => d.status === 'OFFLINE');
    if (offline.length > 0 && Math.random() > 0.7) {
      const d = offline[Math.floor(Math.random() * offline.length)];
      this.mockDriverPool.update(p => p.map(x => x.driverId === d.driverId ? { ...x, status: 'AVAILABLE' as const } : x));
    }
  }

  private seedMockNotifications() {
    const seed: RealtimeNotification[] = [
      { id: '1', type: 'TRIP_UPDATE', title: 'Trip Completed', message: 'Trip #10042 completed — $28.75', severity: 'success', timestamp: new Date(Date.now() - 60000), read: false },
      { id: '2', type: 'GEOFENCE_ALERT', title: 'Geofence Alert', message: 'Driver #5 left JFK Airport zone', severity: 'warn', timestamp: new Date(Date.now() - 120000), read: false },
      { id: '3', type: 'COMMISSION', title: 'Payout Processed', message: '$1,250.00 paid to Driver Carlos Rivera', severity: 'success', timestamp: new Date(Date.now() - 300000), read: true },
      { id: '4', type: 'SYSTEM', title: 'Server Healthy', message: 'All services running normally', severity: 'info', timestamp: new Date(Date.now() - 600000), read: true },
    ];
    this.notifications.set(seed);
    this.unreadCount.set(seed.filter(n => !n.read).length);
  }

  private mockDriverStream(driverId: number): Observable<DriverLocation> {
    return interval(2000).pipe(
      map(() => {
        const loc = this.driverLocations().get(driverId);
        if (loc) return loc;
        const d = this.mockDriverPool().find(x => x.driverId === driverId);
        const pt = d ? d.route[d.routeIndex] : { lat: 40.7128, lng: -74.006 };
        return {
          driverId, name: d?.name ?? `Driver #${driverId}`, vehicle: d?.vehicle ?? '', plate: d?.plate ?? '',
          lat: pt.lat, lng: pt.lng, heading: 0, speed: 0,
          status: (d?.status ?? 'AVAILABLE') as any, timestamp: new Date().toISOString(),
        };
      }),
      takeUntil(this.destroy$),
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
